import { useState } from 'react';

import { Clear as ClearIcon } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';

function JDatePicker(props) {
  const {
    onChange,
    options = {},
    clear = false,
    disabled = false,
    label,
    name,
    size = 'small',
    value: controlledValue,
    color = 'primary',
    variant = 'standard',
    sx: sxProp,
    ...otherProps
  } = props;

  const { defaultValue, dateFormat = 'MM/DD/YYYY', placeHolder, minDate, maxDate } = options;

  // Parse initial value
  const parseValue = (val) => {
    if (!val) return null;
    if (typeof val === 'number') {
      return moment(val);
    }
    if (val instanceof Date) {
      return moment(val);
    }
    if (typeof val === 'string') {
      return moment(val);
    }
    if (moment.isMoment(val)) {
      return val;
    }
    return null;
  };

  const [internalValue, setInternalValue] = useState(() => parseValue(defaultValue));
  const [open, setOpen] = useState(false);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? parseValue(controlledValue) : internalValue;

  const handleChange = (newValue) => {
    const momentValue = newValue ? moment(newValue) : null;
    setInternalValue(momentValue);

    if (onChange) {
      onChange(momentValue ? momentValue.toDate() : null);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    handleChange(null);
  };

  // Get display format - moment format works directly with AdapterMoment
  const getFormat = () => {
    if (!dateFormat) return 'MM/DD/YYYY';
    // Just return the format as-is since AdapterMoment uses moment format
    return dateFormat;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        value={value}
        onChange={handleChange}
        disabled={disabled}
        label={label}
        format={getFormat()}
        minDate={minDate ? moment(minDate) : undefined}
        maxDate={maxDate ? moment(maxDate) : undefined}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            size,
            name,
            color,
            placeholder: placeHolder || 'Select date',
            onClick: () => !disabled && setOpen(true),
            variant,
            sx: {
              width: 150,
              '& .MuiInputBase-root': {
                width: '100%',
              },
              '.MuiInputBase-input': {
                fontWeight: 900,
                color: 'primary.main',
                fontFamily: 'AvenirNext',
              },
              ...(disabled && {
                backgroundColor: '#ededed',
                '& .MuiInputBase-input': {
                  color: '#8d8d8d',
                },
              }),
              ...sxProp,
            },
            InputProps: {
              endAdornment:
                clear && value ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            },
          },
          field: { readOnly: true },
        }}
        {...otherProps}
      />
    </LocalizationProvider>
  );
}

export default JDatePicker;
